<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catCyan" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-spa" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catCyan"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ treatments.length }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar tratamentos</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-spa-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum tratamento registrado ainda.
        </p>
        <v-btn small color="catCyan" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar tratamento
        </v-btn>
      </div>

      <!-- Lista -->
      <div v-else class="treatments-list">
        <v-chip
          v-for="t in treatments"
          :key="t.id"
          class="treatment-chip"
          color="#ECFEFF"
          text-color="catCyan"
          label
        >
          <v-icon left small>mdi-spa</v-icon>
          <span class="chip-text">{{ t.treatment }}</span>
          <v-icon
            v-if="t.catalog_ref || t.complementary_treatment_catalog_id"
            x-small
            color="catCyan"
            class="ml-1 flex-shrink-0"
            title="Do catálogo"
          >
            mdi-check-decagram
          </v-icon>
        </v-chip>
      </div>
    </v-card-text>

    <EditComplementaryTreatmentsDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :treatments="treatments"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getTreatmentsByPatient } from "@/services/complementaryTreatmentService";
import EditComplementaryTreatmentsDialog from "@/features/patients/profile-dashboard/dialogs/EditComplementaryTreatmentsDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "ComplementaryTreatmentsCard",

  components: {
    EditComplementaryTreatmentsDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      treatments: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.treatments || []).length;
    },
    hasData() {
      return this.treatments.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.treatments = await dashboardStore.section(this.patientId, "complementary_treatments", () => getTreatmentsByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar tratamentos complementares:", error);
        this.treatments = [];
      }
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Ciano (Tratamentos Complementares) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #a5f3fc !important;
  box-shadow: 0 2px 10px rgba(8, 145, 178, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #67e8f9 !important;
  box-shadow: 0 6px 20px rgba(8, 145, 178, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
  border-bottom: 1px solid #a5f3fc;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-cyan);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #67e8f9;
  border-radius: 10px;
}

.treatments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.treatment-chip {
  font-size: 12px !important;
  font-weight: 600 !important;
  height: auto !important;
  min-height: 38px !important;
  padding: 8px 12px !important;
  white-space: normal !important;
  overflow-wrap: break-word;
  word-break: normal;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 10px !important;
}

.chip-text {
  flex: 1;
  text-align: left;
  line-height: 1.4;
}
</style>
